/**
 * Mobile Bottom Nav Module
 * Provides a 5-tab bottom navigation that mirrors the desktop rail (.rail)
 * for viewports < 768px where the rail is hidden by mobile.css.
 *
 * Without this module, mobile users have no UI to switch modes after entering
 * any single mode (rail hidden, no fallback). This module:
 *   - Injects <nav class="ws-mobile-nav"> into .app as the auto grid row
 *     (mobile.css :17 sets .app grid-template-rows: 1fr auto for mobile)
 *   - Mounts only when matchMedia mobile breakpoint matches (sourced from
 *     utils/viewport.js, single SoT aligned with mobile.css); unmounts on
 *     resize past breakpoint
 *   - Routes clicks through setMode() so all router invariants
 *     (lifecycle, right-panel tabs, data-mode pillar scoping) stay intact
 *   - Subscribes to MODE_ACTIVATED to keep its active state in sync with the
 *     desktop rail and any external mode change (URL ?mode=, programmatic)
 *
 * CSS already exists in apps/creator-hub/css/mobile.css :79-112 (.ws-mobile-nav,
 * .ws-mobile-nav-btn) — this module is the missing JS injection that the CSS
 * was waiting for.
 */

import { setMode } from '../../core/router.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { getMobileMatchMedia } from '../../utils/viewport.js'

const NAV_ITEMS = [
  { mode: 'create',   icon: 'magic-wand',    label: 'Create' },
  { mode: 'design',   icon: 'paint-brush',   label: 'Design' },
  { mode: 'gallery',  icon: 'images-square', label: 'Gallery' },
  { mode: 'products', icon: 't-shirt',       label: 'Products' },
  { mode: 'me',       icon: 'user-circle',   label: 'Me' },
]

let navEl = null
let mq = null
let modeUnsub = null

function buildNav() {
  const nav = document.createElement('nav')
  nav.className = 'ws-mobile-nav'
  nav.setAttribute('aria-label', 'Mode navigation')
  nav.innerHTML = NAV_ITEMS.map((item) => `
    <button class="ws-mobile-nav-btn" data-mode="${item.mode}" aria-label="${item.label}">
      <i class="ph ph-${item.icon}"></i>
      <span>${item.label}</span>
    </button>
  `).join('')

  nav.addEventListener('click', (e) => {
    const btn = e.target.closest('.ws-mobile-nav-btn[data-mode]')
    if (!btn) return
    setMode(btn.dataset.mode)
  })

  return nav
}

function syncActive(mode) {
  if (!navEl) return
  navEl.querySelectorAll('.ws-mobile-nav-btn[data-mode]').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.mode === mode)
  })
}

function getCurrentMode() {
  const activeRail = document.querySelector('.rail-btn.active[data-mode]')
  return activeRail ? activeRail.dataset.mode : 'create'
}

function mount() {
  if (navEl) return
  const app = document.querySelector('.app')
  if (!app) return
  navEl = buildNav()
  app.appendChild(navEl)
  syncActive(getCurrentMode())
}

function unmount() {
  if (!navEl) return
  navEl.remove()
  navEl = null
}

function handleMqChange(e) {
  if (e.matches) mount()
  else unmount()
}

export function initMobileNav() {
  mq = getMobileMatchMedia()
  if (mq.matches) mount()

  if (mq.addEventListener) {
    mq.addEventListener('change', handleMqChange)
  } else if (mq.addListener) {
    // Safari < 14
    mq.addListener(handleMqChange)
  }

  // Stay in sync with rail / URL / programmatic mode changes
  modeUnsub = eventBus.on(EVENTS.MODE_ACTIVATED, syncActive)
}

export function destroyMobileNav() {
  unmount()
  if (mq) {
    if (mq.removeEventListener) mq.removeEventListener('change', handleMqChange)
    else if (mq.removeListener) mq.removeListener(handleMqChange)
    mq = null
  }
  if (modeUnsub) {
    modeUnsub()
    modeUnsub = null
  }
}

export default { initMobileNav, destroyMobileNav }
